import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { stripeAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await stripeAPI.getStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        setPaymentData(response.data);
        setStatus('success');
        refreshUser();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts >= maxAttempts - 1) {
        setStatus('error');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F2C] flex items-center justify-center p-4">
      <Card className="glass-card w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <div className="animate-fade-in">
              <Loader2 className="w-16 h-16 text-[#00BFA6] mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-white font-['Outfit'] mb-2">
                Verificando pago...
              </h1>
              <p className="text-white/60">Por favor espera mientras confirmamos tu pago</p>
            </div>
          )}

          {status === 'success' && (
            <div className="animate-slide-up">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white font-['Outfit'] mb-2">
                ¡Pago exitoso!
              </h1>
              <p className="text-white/60 mb-6">
                Tu recarga de ${paymentData?.amount_total?.toFixed(2)} {paymentData?.currency?.toUpperCase()} ha sido procesada.
              </p>
              <Link to="/wallet">
                <Button className="btn-primary w-full">
                  Ir a mi Wallet
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="animate-slide-up">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white font-['Outfit'] mb-2">
                Error en el pago
              </h1>
              <p className="text-white/60 mb-6">
                No pudimos verificar tu pago. Por favor intenta de nuevo o contacta soporte.
              </p>
              <Link to="/wallet">
                <Button variant="outline" className="btn-secondary w-full">
                  Volver a Wallet
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
